import { ITranscriptionManager, ITranscription } from '../interfaces/ITranscriptionManager';
import 'isomorphic-fetch';
import { IStoredTranscription, TranscriptionStorageType } from '../interfaces/IStoredTranscription';
import { SpeakerNameManager } from '../manager/speakerNameManager/SpeakerNameManager';
import { HostAppSettingName, HostAppSettings } from '../office-js/HostAppSettings';
import { LogEvents } from '../../utils/LogEvents';
import { DurationMeasureType } from '../interfaces/ILogEvent';
import { DocumentStorageKeys } from '../office-js/telemetry/DocumentStorageKeys';
import { DocumentStorage } from '../office-js/DocumentStorage';
import { ErrorStringFromException, isNullOrUndefined } from '../../utils/utils';
import { TranscriptionData } from '../utils/TranscriptionData';

export class SingleTranscriptionDocumentStorageManager implements ITranscriptionManager {
    private documentStorage: DocumentStorage;
    private transcriptionData: TranscriptionData | null = null;

    public constructor(documentStorage: DocumentStorage, storedTranscription?: IStoredTranscription) {
        if (isNullOrUndefined(documentStorage)) {
            throw new Error("Document Storage can't be null");
        }
        if (storedTranscription !== undefined) {
            this.transcriptionData = new TranscriptionData(storedTranscription);
        }
        this.documentStorage = documentStorage;
    }

    public GetTranscription(): ITranscription {
        if (this.transcriptionData == null) {
            return {
                transcriptSegments: [],
            };
        }
        return this.transcriptionData.GetTranscription();
    }

    public async UpdateTranscriptionSegment(segmentIndex: number, segmentText: string, speakerName: string | null, updateAll: boolean) {
        if (this.transcriptionData != null) {
            let transcription: ITranscription = this.transcriptionData.GetTranscription();

            if (isNullOrUndefined(transcription.transcriptSegments))
                throw new Error("this.transcription.transcriptSegments is null");

            if (segmentIndex < 0 || segmentIndex >= transcription.transcriptSegments.length)
                throw new Error('Invalid segment index');

            transcription.transcriptSegments[segmentIndex].text = segmentText;

            let speakerNameManager: SpeakerNameManager = this.transcriptionData.GetSpeakerNameManager();
            if (speakerNameManager != null && speakerName != null)
                speakerNameManager.UpdateSpeakerName(segmentIndex, speakerName, updateAll);

            await this.WriteStoredTranscription(this.GetStoredTranscription());
        }
    }

    public GetStoredTranscription(): IStoredTranscription {
        if (this.transcriptionData == null) {
            return {
                storageType: TranscriptionStorageType.DocumentStorage,
                descriptor: {},
            };
        }
        return this.transcriptionData.GetStoredTranscription();
    }

    public async WriteTranscriptionToStorage(storedTranscription: IStoredTranscription): Promise<void> {
        if (storedTranscription != null) {
            this.transcriptionData = new TranscriptionData(storedTranscription);
            await this.WriteStoredTranscription(storedTranscription);
        }
    }

    public async DeleteTranscription() {
        this.transcriptionData = null;
        await this.DeleteStoredTranscription();
    }

    public IsMultiTranscriptStorageSupported(): boolean {
        return false;
    }

    public GetStorageVersion(): number {
      return 0; // Please don't change it, 0 signifies IStoredTranscription.
    }

    private async WriteStoredTranscription(storedTranscription: IStoredTranscription): Promise<void> {
        LogEvents.DocumentStorage.Start();
        LogEvents.DocumentStorage.BeginDurationMeasure(DurationMeasureType.Normal, DocumentStorageKeys.WriteStoredTranscriptionDuration);

        try {
            await this.documentStorage.WriteTranscribeContentToClient(JSON.stringify(storedTranscription));
            HostAppSettings.Set(HostAppSettingName.HasStoredTranscription, true);
            HostAppSettings.Set(HostAppSettingName.StoredTranscription, storedTranscription);
            LogEvents.DocumentStorage.End();
        } catch (e) {
            LogEvents.DocumentStorage.End([{ key: DocumentStorageKeys.FailureReason, value: ErrorStringFromException(e) }]);
            throw e;
        }
    }

    private async DeleteStoredTranscription(): Promise<void> {
        LogEvents.DocumentStorage.Start();
        LogEvents.DocumentStorage.BeginDurationMeasure(DurationMeasureType.Normal, DocumentStorageKeys.DeleteStoredTranscriptionDuration);

        try {
            await this.documentStorage.DeleteTranscribeContentFromClient();
            HostAppSettings.Set(HostAppSettingName.HasStoredTranscription, false);
            HostAppSettings.Set(HostAppSettingName.StoredTranscription, null);
            LogEvents.DocumentStorage.End();
        } catch (e) {
            LogEvents.DocumentStorage.End([{ key: DocumentStorageKeys.FailureReason, value: ErrorStringFromException(e) }]);
            throw e;
        }
    }
}